import React, { forwardRef, useEffect, useRef, useCallback, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const Page = forwardRef((props, ref) => {
    return (
        <div className="page" ref={ref}>
            <div className="page-content w-full h-full bg-transparent flex items-center justify-center overflow-hidden relative">
                <img
                    src={props.image}
                    alt={`Page ${props.number}`}
                    className="w-full h-full object-contain"
                />
                {/* Page number removed as per user request */}
            </div>
        </div>
    );
});

const MagazineViewer = ({ isOpen, onClose, pages = [] }) => {
    const bookRef = useRef();
    // Strict mode: Mobile (<768px) -> Scroll, Desktop (>=768px) -> Flip
    const [viewMode, setViewMode] = useState(typeof window !== 'undefined' && window.innerWidth < 768 ? 'scroll' : 'flip');

    // Preload images for faster viewing
    useEffect(() => {
        if (isOpen && pages.length > 0) {
            // Priority preload first 5 pages
            const toPreload = pages.slice(0, 5);
            toPreload.forEach(src => {
                const img = new Image();
                img.src = src;
            });
        }
    }, [isOpen, pages]);

    // Handle Resize to enforce strict mode
    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth < 768;
            setViewMode(isMobile ? 'scroll' : 'flip');
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Sound Effect from external source
    const audioRef = useRef(new Audio('https://www.soundjay.com/books/sounds/page-flip-01a.mp3'));

    useEffect(() => {
        const audio = audioRef.current;
        audio.preload = "auto";

        return () => {
            audio.pause();
            audio.currentTime = 0;
        };
    }, []);

    const playFlipSound = useCallback(() => {
        if (viewMode !== 'flip') return; // No sound in scroll mode
        const audio = audioRef.current;
        if (audio) {
            audio.currentTime = 0;
            const playPromise = audio.play();

            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.warn("Audio play failed (autoplay block?):", error);
                });
            }
        }
    }, [viewMode]);

    // Handle ESC key to close
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    if (!isOpen) return null;

    // Scroll Mode Content
    const ScrollModeContent = () => (
        <div className="w-full h-full overflow-y-auto overflow-x-hidden flex flex-col items-center py-20 px-4 space-y-4">
            {pages.map((url, index) => (
                <div key={index} className="w-full max-w-3xl shadow-lg relative bg-white">
                    <img
                        src={url}
                        alt={`Page ${index + 1}`}
                        className="w-full h-auto block"
                        loading={index < 2 ? "eager" : "lazy"} // Eager load first 2
                    />
                </div>
            ))}
            <div className="text-white/50 text-sm pb-10">Son Sayfa</div>
        </div>
    );

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center overflow-hidden"
            >
                {/* Top Bar: Title, Mode Toggle & Close */}
                <div className="absolute top-0 left-0 right-0 z-[70] flex justify-between items-center p-4 md:p-6 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
                    <div className="text-white/80 font-medium text-lg pointer-events-auto flex items-center gap-4">
                        <span>Dergi Okuma Modu</span>
                    </div>

                    <button
                        onClick={onClose}
                        className="pointer-events-auto text-white/70 hover:text-cyan-400 hover:bg-white/10 p-2 rounded-full transition-all"
                    >
                        <X size={32} strokeWidth={1.5} />
                    </button>
                </div>

                {viewMode === 'scroll' ? (
                    <ScrollModeContent />
                ) : (
                    /* Flip Mode (Desktop Optimized) */
                    <TransformWrapper
                        initialScale={1}
                        minScale={1}
                        maxScale={4}
                        centerOnInit={true}
                        wheel={{ step: 0.1 }}
                        doubleClick={{ disabled: true }}
                    >
                        {({ zoomIn, zoomOut, resetTransform, scale }) => (
                            <>
                                {/* Navigation Buttons (Arrows) - Only for Flip Mode */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        bookRef.current?.pageFlip().flipPrev();
                                    }}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 z-[60] text-cyan-400 hover:text-cyan-300 active:scale-95 bg-black/60 backdrop-blur-md p-3 rounded-full border border-white/10 shadow-lg transition-all hidden md:block" // Hidden on mobile initially, though flip mode might be active if forced
                                >
                                    <ChevronLeft className="w-8 h-8 md:w-10 md:h-10" strokeWidth={2} />
                                </button>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        bookRef.current?.pageFlip().flipNext();
                                    }}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 z-[60] text-cyan-400 hover:text-cyan-300 active:scale-95 bg-black/60 backdrop-blur-md p-3 rounded-full border border-white/10 shadow-lg transition-all hidden md:block"
                                >
                                    <ChevronRight className="w-8 h-8 md:w-10 md:h-10" strokeWidth={2} />
                                </button>

                                {/* Main Flip Content Area */}
                                <div className="w-full h-full flex items-center justify-center">
                                    <TransformComponent
                                        wrapperClass="!w-full !h-full flex items-center justify-center"
                                        contentClass="!w-full !h-full flex items-center justify-center"
                                    >
                                        <div
                                            className="relative shadow-[0_35px_60px_-15px_rgba(0,0,0,0.8)]"
                                            style={{
                                                height: '80vh', // Reduced from 85vh to give more space for controls
                                                maxHeight: '900px',
                                                aspectRatio: '1.5',
                                                cursor: scale > 1 ? 'grab' : 'default'
                                            }}
                                        >
                                            {pages.length > 0 ? (
                                                <HTMLFlipBook
                                                    width={400}
                                                    height={565}
                                                    size="stretch"
                                                    minWidth={100}
                                                    maxWidth={2000}
                                                    minHeight={140}
                                                    maxHeight={2828}
                                                    maxShadowOpacity={0.5}
                                                    showCover={true}
                                                    mobileScrollSupport={true}
                                                    ref={bookRef}
                                                    onFlip={playFlipSound}
                                                    useMouseEvents={false} /* Disable drag/swipe */
                                                    clickEvent={false}     /* Disable click-to-flip */
                                                    className="w-full h-full bg-transparent"
                                                >
                                                    {pages.map((url, index) => (
                                                        <Page key={index} number={index + 1} image={url} />
                                                    ))}
                                                </HTMLFlipBook>
                                            ) : null}
                                        </div>
                                    </TransformComponent>
                                </div>

                                {/* Bottom Controls Bar (Zoom etc) - Only for Flip Mode usually */}
                                <div className="absolute bottom-8 right-8 z-[70] flex flex-col items-center gap-2">
                                    <div className="text-white/40 text-xs font-light tracking-wider uppercase opacity-0 animate-fadeIn delay-1000 hidden md:block">
                                        {scale > 1 ? 'Kaydırmak için sürükleyin' : 'Yakınlaştırmak için butonları kullanın'}
                                    </div>

                                    <div className="flex items-center gap-6 bg-black/40 backdrop-blur-xl px-8 py-3 rounded-full border border-white/5 shadow-2xl ring-1 ring-white/10">
                                        <button onClick={() => zoomOut()} className="text-white/80 hover:text-white hover:scale-110 transition-all active:scale-95">
                                            <ZoomOut size={22} strokeWidth={1.5} />
                                        </button>

                                        <div className="w-px h-6 bg-white/10" />

                                        <button onClick={() => resetTransform()} className="text-white/80 hover:text-emerald-400 hover:rotate-180 transition-all duration-500" title="Görünümü Sıfırla">
                                            <RotateCcw size={20} strokeWidth={1.5} />
                                        </button>

                                        <div className="w-px h-6 bg-white/10" />

                                        <button onClick={() => zoomIn()} className="text-white/80 hover:text-white hover:scale-110 transition-all active:scale-95">
                                            <ZoomIn size={22} strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </TransformWrapper>
                )}
            </motion.div>
        </AnimatePresence>
    );
};

export default MagazineViewer;
